/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types } from 'mobx-state-tree';


const HeaderItem = types.model({
	id: types.string,
	name: ''
});

const DefaultStatValues = types.map( types.frozen() );

/**
* Class representing model for live widget stats
*
* Essentially, the structure of the data being held is:
*  {
*	header:[ 									--> describes the header to use when displaying stats
*		{ id: 'min', name: 'Min' },				--> each statistic has an id and a display name
*		{ id: 'max', name: 'Max' },
*		{ id: 'avg', name: 'Average' },
*		{ id: 'total', name: 'Total' },
*		{ id: 'totalPercent', name: '%Total' }
*	],
*	measures: {
*		measure: {								--> for each measure, provide the stats
*			min: '',							--> for each stat id, provide the value
*			max: '',
*			avg: '',
*			total: '',
*			totalPercent: ''
*		}
*	}
*}
*/
const StatsModel = types.model({
	header: types.union( types.array( HeaderItem ), types.undefined ),
	measures: types.union( types.map( DefaultStatValues ), types.undefined),
	_env: types.frozen()//workaround - can't use env which is different from parent's env
}).views((self) => ({
	get headers() {
		const headers = [self.measureLabel];
		const itemHeaders = self.header || [];
		itemHeaders.forEach( (entry) => {
			headers.push( entry.name );
		});
		return headers;
	},

	/**
     * return stats in a format that makes it easy to render
     */
	get orderedValues() {
		const values = [];
		const itemHeaders = self.header || [];
		const itemValuesMap = self.measures;

		//compute stats rows
		itemValuesMap.forEach( (measureDetail/*, measureName*/) => {
			const row = [];
			const meta = measureDetail.get('metadata') || {};
			row.push(meta.label);  //first column is measure name, rest are the values
			itemHeaders.forEach( (header) => {
				const statId = header.id;
				if( statId !== 'metadata' && statId !== 'totalPercent') {
					const val = measureDetail.get(statId);
					if( typeof val === 'string' || val instanceof String ) {
						row.push( val || '-');
					} else {
						row.push( Math.round(val) || '-');
					}
				}
				if(statId === 'totalPercent' && measureDetail.get('totalUse') && measureDetail.get('summaryValueUse') ) {
					const totalPercent = Math.round((measureDetail.get('totalUse') /  measureDetail.get('summaryValueUse'))*100);
					row.push(totalPercent);
				} else if(statId === 'totalPercent') {
					row.push('-');
				}
			});
			values.push({ row:row });
		});
		return values;
	}
})).volatile( () => ({
	widgetId: 0,
	showStats: function() {
		return true;
	},
	statsModelUpdateCount: 0,
	newCompareTableStatsEnabled: false
})).actions(self => ({

	afterCreate() {
		self.measureLabel = self._env && self._env.measureLabel || '';
		self.showStats = self._env && self._env.showStats || self.showStats;
		self.newCompareTableStatsEnabled = self._env && self._env.newCompareTableStatsEnabled;
	},

	updateData(data) {
		if(data && data.header && data.measures ){
			self.header = data.header;
			self.measures = data.measures;
			self.lastCategorySelection = data.lastCategorySelection;
		}
	}
}));

export default StatsModel;

const SelectionValues = types.model({
	values: types.array(types.maybeNull(types.number)),
	metadata: types.union( types.frozen(), types.undefined),
	//detailRowCount > 0 should be checked to determine if weightedAvg is valid.
	detailRowCountUse: types.optional(types.number, 0),
	detailSumUse: types.optional(types.number, 0),
	filteredSummaryValue: types.optional(types.string, '-'),
	filteredSummaryValueUse: types.maybeNull(types.number),
	weightedAvgUse: types.optional(types.number, 0)
});

const LastCategorySelection = types.array(types.union(types.string, types.number, types.null));

export const SelectionStatsModel = StatsModel
	.named('SelectionStatsModel')
	.props({
		measures: types.union( types.map( SelectionValues ), types.undefined),
		lastCategorySelection: types.union( LastCategorySelection, types.undefined )
	}).actions(self => ({

		updateData(data) {
			self.header = data.header;
			self.measures = data.measures;
			self.lastCategorySelection = data.lastCategorySelection;
		}
	}));



// WEBPACK FOOTER //
// ./src/js/features/compare/StatsModel.js